import type { UiNode, UiNodeInputAttributes, UiText } from '@ory/client';
import { useState } from 'react';
import ActionButton from '@/components/Button/ActionButton';
import ResponseMessage from '@/components/Error/ResponseMessage';
import { Spacer, VStack } from '@/components/Layout';
import { useNodeTranslation } from '@/features/auth/hooks/useNodeTranslation';
const PROVIDER_DISPLAY_NAMES: Record<string, string> = {
  google: 'Google',
  microsoft: 'Microsoft'
  // apple: "Apple",
};

/** provider 識別子（node.attributes.value）→ 公式ブランドアバターのアプリ slug */
const PROVIDER_AVATAR_SLUGS: Record<string, string> = {
  google: 'google_workspace',
  microsoft: 'microsoft_entra_id'
};
const BRAND_ASSET_BASE = 'https://asset.dress-code.com/public/apps';
function getProviderDisplayName(provider: string): string {
  return PROVIDER_DISPLAY_NAMES[provider] ?? provider.charAt(0).toUpperCase() + provider.slice(1);
}

/**
 * Kratos は context.provider を小文字で返すため、表示用にキャピタライズする。
 * @ory/client の UiText.context は object 型のため、Record にキャストして参照する。
 */
function capitalizeProviderInLabel(label: UiText): UiText {
  const ctx = label.context as Record<string, unknown> | undefined;
  const provider = ctx?.provider;
  if (typeof provider !== 'string') return label;
  return {
    ...label,
    context: {
      ...label.context,
      provider: getProviderDisplayName(provider)
    } as object
  };
}
type Props = {
  node: UiNode;
  disabled?: boolean;
};

/**
 * OIDC 専用の submit ボタンコンポーネント
 *
 * プロバイダー識別子（node.attributes.value）から公式ブランドアバターと表示名を引く。
 * ロゴは各社の公式画像をそのまま用い、暗色ボタン上でも視認できるよう白背景の
 * チップに載せる。
 *
 * @see https://www.ory.com/docs/kratos/concepts/ui-user-interface#submit-input-node
 */
export function OidcSubmitInput({
  node,
  disabled
}: Props) {
  const {
    translateUiText
  } = useNodeTranslation();
  const [wasClicked, setWasClicked] = useState(false);
  const attrs = node.attributes as UiNodeInputAttributes;
  if (attrs.type !== 'submit') {
    return null;
  }
  const labelUiText = node.meta?.label ? capitalizeProviderInLabel(node.meta.label) : undefined;
  const label = labelUiText ? translateUiText(labelUiText) : undefined;
  const provider = typeof attrs.value === 'string' ? attrs.value : undefined;
  const avatarSlug = provider ? PROVIDER_AVATAR_SLUGS[provider] : undefined;
  const avatarUrl = avatarSlug ? `${BRAND_ASSET_BASE}/${avatarSlug}/avatar` : undefined;
  const errorMessages = node.messages?.filter(msg => msg.type === 'error').map(msg => translateUiText(msg));

  // OIDC は全画面リダイレクト。押したボタンだけをローディング表示にし、
  // 他プロバイダーはフォーム全体の disabled で巻き込まず独立して押せるままにする。
  const isLoading = wasClicked && !!disabled;
  return <VStack className="w-full gap-0">
      <ActionButton type="submit" name={attrs.name} value={attrs.value as string} disabled={attrs.disabled} isLoading={isLoading} onClick={() => setWasClicked(true)} variant="default" className="w-full">
        <span className="flex items-center justify-center gap-2">
          {avatarUrl && <span className="flex size-5 shrink-0 items-center justify-center rounded-sm bg-white p-0.5">
              <img src={avatarUrl} alt="" className="size-full object-contain" />
            </span>}
          {label}
        </span>
      </ActionButton>
      {errorMessages && errorMessages.length > 0 && <>
          <Spacer variant="xxs" />
          <ResponseMessage type="error" size="xs">
            {errorMessages[0]}
          </ResponseMessage>
        </>}
    </VStack>;
}